import { useState, useEffect } from 'react';

interface ResponsiveBreakpoints {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  width: number;
  height: number;
}

export const useResponsive = (): ResponsiveBreakpoints => {
  const [dimensions, setDimensions] = useState<ResponsiveBreakpoints>({
    isMobile: false,
    isTablet: false,
    isDesktop: true,
    width: 1920,
    height: 1080
  });

  useEffect(() => {
    const updateDimensions = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      
      setDimensions({
        width,
        height,
        isMobile: width < 768,
        isTablet: width >= 768 && width < 1024,
        isDesktop: width >= 1024
      });
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  return dimensions;
};

export const getResponsiveWindowProps = (screenWidth: number, screenHeight: number) => {
  if (screenWidth < 768) {
    // Mobile: Full screen
    return {
      position: { x: 0, y: 0 },
      size: { width: screenWidth, height: screenHeight },
      isMaximized: true
    };
  } else if (screenWidth < 1024) {
    // Tablet: Large windows with padding
    const padding = 20;
    return {
      position: { x: padding, y: padding },
      size: { 
        width: screenWidth - padding * 2, 
        height: screenHeight - padding * 2 
      },
      isMaximized: false
    };
  } else {
    // Desktop: Smart positioning
    const maxWidth = Math.min(1200, screenWidth * 0.85);
    const maxHeight = Math.min(800, screenHeight * 0.85);
    
    return {
      position: { 
        x: (screenWidth - maxWidth) / 2, 
        y: (screenHeight - maxHeight) / 2 
      },
      size: { width: maxWidth, height: maxHeight },
      isMaximized: false
    };
  }
};

interface CascadeParams {
  previousPosition: { x: number; y: number } | null;
  offset: number;
  viewportWidth: number;
  viewportHeight: number;
  windowWidth: number;
  windowHeight: number;
  menuBarHeight?: number;
}

export const getCascadePosition = (params: CascadeParams): { x: number; y: number } => {
  const {
    previousPosition,
    offset,
    viewportWidth,
    viewportHeight,
    windowWidth,
    windowHeight,
    menuBarHeight = 28
  } = params;

  // Desktop only (viewport width >= 1024px)
  if (viewportWidth < 1024) {
    // Fallback to centered position for non-desktop
    const centered = getResponsiveWindowProps(viewportWidth, viewportHeight);
    return centered.position;
  }

  // If no previous position, return centered position
  if (!previousPosition) {
    const centered = getResponsiveWindowProps(viewportWidth, viewportHeight);
    return centered.position;
  }

  // Calculate cascaded position
  let newX = previousPosition.x + offset;
  let newY = previousPosition.y + offset;

  // Check viewport bounds
  const minX = 0;
  const minY = menuBarHeight;
  const maxX = viewportWidth - windowWidth;
  const maxY = viewportHeight - windowHeight;

  // If cascaded position would push window off-screen, reset to smart default
  if (newX > maxX || newY > maxY || newX < minX || newY < minY) {
    // Reset to smart default position
    newX = 40;
    newY = menuBarHeight + 28; // 28px menu bar + 28px padding
  }

  return { x: newX, y: newY };
};
